<script lang="ts" setup>
import { computed, onMounted, Ref, ref, toRefs } from 'vue';
import Column from 'primevue/column';
import Chip from 'primevue/chip';
import DataTable from 'primevue/datatable';
import ArchiveSwitch from '@/components/ArchiveSwitch';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import { useArchiveStore, useOrganizationParentStore } from '@/store';
import { OrganizationParentsAssociatedInterface } from '@/types';
import { can } from '@/composables';
import { PERMISSIONS } from '@/enum/permissions';

const props = defineProps({
  organizationId: {
    type: Number,
    default: null,
  },
});

const { organizationId } = toRefs(props);

onMounted(() => {
  if (can([PERMISSIONS.ORGANIZATION_PARENTS_INDEX])) getParents(false);
});

const organizationParentStore = useOrganizationParentStore();
const archiveStore = useArchiveStore();
const parents: Ref<OrganizationParentsAssociatedInterface[]> = ref([]);
const parentsCount: Ref<number> = computed(() => (parents.value ? parents.value.length : 0));
const parentsModalOpen = ref(false);
const archivedVisible = ref(false);

const getParents = async (showArchived: boolean) => {
  const filterArchived = showArchived ? '?archived=1' : '';
  parents.value = await organizationParentStore.getParentsForTable(organizationId.value, filterArchived);
};

const createMenuModel = (item: OrganizationParentsAssociatedInterface) => {
  const menuItems = [
    {
      label: 'Remove Parents',
      visible: can([PERMISSIONS.ORGANIZATION_DETACH_PARENTS]),
      command: () => {
        detachParents([item.id], item.full_name);
      },
    },
  ];
  if (item.archived_at) {
    menuItems.push({
      label: 'Unarchive',
      visible: can([PERMISSIONS.PEOPLE_ARCHIVE]),
      command: async () => {
        await archiveStore.unarchive(item.id);
        archiveStore.doToast(`${item.full_name} has been restored`, 'Unarchived');
      },
    });
  }
  return menuItems;
};

const attachParents: (ids: number[]) => Promise<void> = async (ids) => {
  await organizationParentStore.attachParents({ parents: ids }, organizationId.value);
  await getParents(false);
  organizationParentStore.doToast(`Parent(s) has been added`, 'Added');
};

const detachParents: (ids: number[], parentName: string) => Promise<void> = async (ids, parentName) => {
  await organizationParentStore.dettachParents({ parents: ids }, organizationId.value);
  await getParents(false);
  organizationParentStore.doToast(`${parentName} has been removed from organization`, 'Removed');
};

const openAddParents = async () => {
  parentsModalOpen.value = true;
  organizationParentStore.page = 0;
  await organizationParentStore.getAddParentsModalData(organizationId.value);
};

const onLoadMoreParents = async () => await organizationParentStore.getAddParentsModalData(organizationId.value);

const onCancelAdd = () => (parentsModalOpen.value = false);

const onSaveAdd = (parents: { name: string; value: number }[]) => {
  let parentsIds = parents.map((parents) => {
    return parents.value;
  });
  attachParents(parentsIds);
  parentsModalOpen.value = false;
};

const showArchived = () => {
  archivedVisible.value = true;
  organizationParentStore.showArchived = true;
  getParents(true);
};

const hideArchived = () => {
  archivedVisible.value = false;
  organizationParentStore.showArchived = false;
  getParents(false);
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Parents</h2>
      <Chip>{{ parentsCount }}</Chip>
      <ArchiveSwitch
        v-if="can([PERMISSIONS.ORGANIZATIONS_PARENTS_SHOW_ARCHIVE])"
        @enable="showArchived"
        @disable="hideArchived"
      />
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.ORGANIZATION_ATTACH_PARENTS])"
          id="add-button"
          type="text"
          size="small"
          text="Add Parents"
          icon-left="add"
          @click="openAddParents"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="parents"
        :global-filter-fields="['full_name']"
        :row-class="({ archived_at }) => (archived_at ? 'grey-row' : null)"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="organizationParentStore.loading"
        data-key="full_name"
        responsive-layout="scroll"
        scroll-height="440px"
        state-key="session-store-facilitators-table"
        state-storage="session"
        :class="{ 'table-loading': organizationParentStore.loading }"
      >
        <template v-if="parentsCount">
          <Column
            body-style="max-width: 20rem"
            field="full_name"
            header="Parent Name"
            header-style="max-width: 20rem; color: #3B4242;"
            sortable
          >
            <template #body="{ data }">
              <Icon
                v-if="data.archived_at"
                v-tooltip.top="{
                  value: 'Archived',
                  escape: true,
                  fitContent: true,
                }"
                name="info"
                class="mr-1 text-color-gray-dark text-base cursor-pointer"
              />
              <RouterLink
                v-if="can([PERMISSIONS.PEOPLE_SHOW])"
                :to="`/user/${data.id}`"
                class="font-semibold"
                v-text="data.full_name"
              />
              <span v-else class="font-semibold" v-text="data.full_name" />
            </template>
          </Column>
          <Column></Column>
          <Column
            body-style="justify-content: center; font-size: .8725rem; max-width: 10rem;"
            header-style="justify-content: center; max-width: 10rem"
            field="id"
            header="Actions"
          >
            <template #body="{ data }">
              <ActionMenu :menu-items="createMenuModel(data)" icon="more_horiz" />
            </template>
          </Column>
        </template>
        <template #empty>Parents not assigned</template>
      </DataTable>
      <AddPersonModal
        v-if="parentsModalOpen && can([PERMISSIONS.ORGANIZATION_ATTACH_PARENTS])"
        title="Add parents"
        :items="organizationParentStore.parentModalData"
        :loading="organizationParentStore.modalDataLoading"
        :searchable="true"
        list-header-text="Parent name"
        button-text="Add Parents"
        placeholder="Search Parents"
        person-type="parents"
        @add-items="onSaveAdd"
        @cancel="onCancelAdd"
        @load-more-items="onLoadMoreParents"
      />
    </template>
  </Card>
</template>

<style lang="scss" scoped>
.p-card {
  min-width: calc(100% - 1.5rem);
  .p-card-body {
    padding-bottom: 0px;
  }
}
</style>
